/** @jsx jsx */
import { jsx } from '@front10/base-elements';
import { useTranslation } from '@front10/utils';
import { useMemo, useState, useRef } from 'react';
import { FilterButton } from '../../lab/FilterButton';
import { FlexibleFilterContainer } from '../../lab/FlexibleFilterContainer';
import { Listbox } from '../../lab/Listbox';
import { PeopleCounter, PeopleInfo, usePeopleCounter } from '../../lab/PeopleCounter';
import { useIsMobileView } from '@front10/utils';
import { SelectProps } from '../../lab/utils';
import { PassengersProps } from './bookingBusinessRules';
import { BookingComponentsConfigurations } from './bookingComponentsConfigurations';
import { Panels } from './Panels';

export interface FlightInfo {
  passengers: PeopleInfo;
  travelClass: string;
}

type FlightInfoSelectorProps = {
  onChange: (value: FlightInfo) => void;
  activateRouteOption?: boolean;
  travelClasses: SelectProps;
  passengers: PassengersProps;
  travelClassConfig?: BookingComponentsConfigurations['travelClassSelector'];
  origin?: string;
  destination?: string;
  enabledDoneButton?: boolean;
  passengersRules?: Array<string>;
};

export function PassengerInfoSelector(props: FlightInfoSelectorProps) {
  const peopleSelector = usePeopleCounter(
    props.passengers,
    (newValue) => {
      if (!persistentMode) {
        props.onChange({ passengers: newValue, travelClass });
      }
    },
    props?.passengersRules
  );
  const defaultValue = {
    passengers: peopleSelector.value,
    travelClass: props.travelClasses.defaultValue
  };
  const labels = useMemo(() => {
    const lookup: Record<string, string> = {};
    for (let travelClass of props.travelClasses.options) {
      lookup[travelClass.value] = travelClass.label;
    }
    return lookup;
  }, [props.travelClasses.options]);
  const translate = useTranslation();
  const [savedState, setSavedState] = useState(defaultValue);
  const [travelClass, setTravelClass] = useState(props.travelClasses.defaultValue);
  const persistentMode = useIsMobileView();
  const showTravelClass = props.travelClassConfig?.enabled ?? true;
  const listboxRef = useRef<any>();

  const label = translate('cmp-flight-info-title', 'Flight Info');
  const value = translate(
    'cmp-passengers-class-label',
    '{passengerCount} passenger, {travelClass}',
    {
      passengerCount: peopleSelector.count,
      travelClass: labels[travelClass]
    }
  );
  const valuePassengerCount = translate(
    'cmp-passengers-class-label',
    '{passengerCount} passenger',
    {
      passengerCount: peopleSelector.count
    }
  );

  const NO_VALUE = -1;
  const SHOW_TRUE_RULE = 0;
  const NO_SHOW_NO_TRUE_RULE = 1;

  function showOption(rule: any, origin: any, destination: any) {
    let modeRule = NO_VALUE;
    const typeRule1 = rule[0];
    const typeRule2 = rule[1];
    const show = rule[2];

    if (typeRule1.length !== 0) {
      let typeRoute = typeRule1[0];
      switch (typeRoute) {
        case 'origin': {
          let countrys = typeRule1[1];
          for (let k = 0; k < countrys.length; k++) {
            if (origin === countrys[k]) {
              modeRule = show ? SHOW_TRUE_RULE : NO_SHOW_NO_TRUE_RULE;
              break;
            }
          }
          if (modeRule === NO_VALUE) {
            let countrys = typeRule2[1];
            if (countrys !== undefined) {
              for (let k = 0; k < countrys.length; k++) {
                if (destination === countrys[k]) {
                  modeRule = show ? SHOW_TRUE_RULE : NO_SHOW_NO_TRUE_RULE;
                  break;
                }
              }
            }
          }
          if (modeRule === NO_VALUE) {
            modeRule = show ? NO_SHOW_NO_TRUE_RULE : SHOW_TRUE_RULE;
          }
          break;
        }
        case 'destination': {
          let countrys = typeRule1[1];
          for (let k = 0; k < countrys.length; k++) {
            if (destination === countrys[k]) {
              modeRule = show ? SHOW_TRUE_RULE : NO_SHOW_NO_TRUE_RULE;
              break;
            }
          }
          if (modeRule === NO_VALUE) {
            let countrys = typeRule2[1];
            if (countrys !== undefined) {
              for (let k = 0; k < countrys.length; k++) {
                if (origin === countrys[k]) {
                  modeRule = show ? SHOW_TRUE_RULE : NO_SHOW_NO_TRUE_RULE;
                  break;
                }
              }
            }
          }
          if (modeRule === NO_VALUE) {
            modeRule = show ? NO_SHOW_NO_TRUE_RULE : SHOW_TRUE_RULE;
          }
          break;
        }
      }
    } else if (typeRule2.length !== 0) {
      let typeRoute = typeRule2[0];

      switch (typeRoute) {
        case 'origin': {
          let countrys = typeRule2[1];
          for (let k = 0; k < countrys.length; k++) {
            if (origin === countrys[k]) {
              modeRule = show ? SHOW_TRUE_RULE : NO_SHOW_NO_TRUE_RULE;
              break;
            }
          }
          if (modeRule === NO_VALUE) {
            modeRule = show ? NO_SHOW_NO_TRUE_RULE : SHOW_TRUE_RULE;
          }
          break;
        }
        case 'destination': {
          let countrys = typeRule2[1];
          for (let k = 0; k < countrys.length; k++) {
            if (destination === countrys[k]) {
              modeRule = show ? SHOW_TRUE_RULE : NO_SHOW_NO_TRUE_RULE;
              break;
            }
          }
          if (modeRule === NO_VALUE) {
            modeRule = show ? NO_SHOW_NO_TRUE_RULE : SHOW_TRUE_RULE;
          }
          break;
        }
      }
    }
    return modeRule;
  }

  function applyBusinessRulesPeopleCounter(
    origin: string | undefined,
    destination: string | undefined,
    options: any | undefined
  ) {
    let newOptions = [...options];
    let element;
    let rule;
    let rules;
    let modeRule = NO_VALUE;
    for (let i = 0; i < options.length; i++) {
      element = options[i];
      if (element.rules !== undefined && element.rules.length !== 0) {
        rules = element.rules;

        for (let j = 0; j < rules.length; j++) {
          rule = rules[j];
          modeRule = showOption(rule, origin, destination);
        }
        if (modeRule === SHOW_TRUE_RULE) {
          newOptions[i].show = true;
        } else {
          newOptions[i].show = false;
        }
        modeRule = NO_VALUE;
      } else {
        newOptions[i].show = true;
      }
    }
    return newOptions;
  }

  function applyBusinessRules(
    origin: string | undefined,
    destination: string | undefined,
    options: any | undefined
  ) {
    let newOptions = [];
    let rules;
    let rule;
    let element;
    let modeRule = NO_VALUE;
    for (let i = 0; i < options.length; i++) {
      element = options[i];
      if (element.rules !== undefined && element.rules.length !== 0) {
        rules = element.rules;
        for (let j = 0; j < rules.length; j++) {
          rule = rules[j];
          modeRule = showOption(rule, origin, destination);
        }
        if (modeRule === SHOW_TRUE_RULE) {
          newOptions.push(element);
        }
        modeRule = NO_VALUE;
      } else {
        newOptions.push(element);
      }
    }
    return newOptions;
  }

  const travelClassOptions = applyBusinessRules(
    props.origin,
    props.destination,
    props.travelClasses.options
  );

  const showtravelClassOptions = showTravelClass && travelClassOptions.length !== 0;

  const findDefaultValue = (travelclass: any) => {
    for (let index = 0; index < travelClassOptions.length; index++) {
      const element = travelClassOptions[index];
      if (element.value === travelclass) {
        return true;
      }
    }
    return false;
  };
  const showDefaultValue = showtravelClassOptions && findDefaultValue(travelClass);
  function setDefaultValue() {
    const findDefaultValueInOptions = findDefaultValue(travelClass);
    if (showtravelClassOptions && findDefaultValueInOptions) {
      return travelClass;
    } else {
      return '';
    }
  }

  return (
    <FlexibleFilterContainer
      variant="layout.popover"
      label={label}
      enabledDoneButton={props.enabledDoneButton}
      labelBottomBtn={translate('cmp-booking-mask-passenger-done-button', 'Done')}
      toggleElement={
        <FilterButton
          value={showDefaultValue ? value : valuePassengerCount}
          aria-label={`${label} ${value}`}
          active={true}
        />
      }
      onSave={() => {
        const state = {
          passengers: peopleSelector.value,
          travelClass,
          passengerTypes: props.passengers
        };
        props.onChange(state);
        setSavedState(state);
      }}
      onDismiss={() => {
        setTravelClass(savedState.travelClass);
        peopleSelector.dispatch({
          type: 'VALUE_SET',
          value: savedState.passengers
        });
      }}
    >
      <Panels>
        {showTravelClass ? (
          <div>
            <Listbox
              ref={listboxRef}
              label={translate('cmp-flight-travel-class-title', 'Travel Class')}
              layout="standard"
              className="travelClass"
              options={travelClassOptions}
              disabled={props.activateRouteOption && (!props.origin || !props.destination)}
              key={Math.floor(Math.random() * 100 + 1)}
              defaultValue={setDefaultValue()}
              focusWhenIsShow={false}
              onChange={(newValue) => {
                setTravelClass(newValue);
                if (!persistentMode) {
                  props.onChange({
                    travelClass: newValue,
                    passengers: peopleSelector.value
                  });
                }
                setTimeout(() => {
                  listboxRef.current?.focusToggleElement();
                }, 20);
              }}
            />
          </div>
        ) : null}
        <PeopleCounter
          origin={props.origin}
          destination={props.destination}
          title={translate('cmp-booking-passenger-title', 'Passengers')}
          options={applyBusinessRulesPeopleCounter(
            props.origin,
            props.destination,
            props.passengers.options
          )}
          dispatch={peopleSelector.dispatch}
          value={peopleSelector.value}
          isEventValid={peopleSelector.isEventValid}
        />
      </Panels>
    </FlexibleFilterContainer>
  );
}
